/**
 * Las barras que dibujan una nota de voz.
 *
 * Se intenta la **forma real**: se decodifica el audio en el navegador del
 * operador y se promedian sus muestras. Los silencios se ven como valles, y dos
 * audios distintos se ven distintos — que es lo que hace útil a la onda y no
 * sólo decorativa.
 *
 * Si el navegador no puede decodificar el formato —Safari falla con Opus dentro
 * de Ogg, que es justo lo que manda WhatsApp— se cae a barras derivadas del
 * identificador del archivo. Se ven parecidas de un vistazo y son estables para
 * el mismo audio, así que nadie queda con un reproductor roto.
 *
 * Nada de esto cuesta: la decodificación ocurre en el navegador de quien mira.
 */

/** Cuántas barras dibuja la onda. Suficientes para leerla, pocas para que entre. */
export const BARRAS = 40;

/**
 * Promedia las muestras en `BARRAS` tramos y normaliza a [0,1].
 *
 * Se usa el promedio del valor absoluto y no el pico: el pico de un tramo lo
 * define una sola muestra, así que basta un chasquido para que toda la barra se
 * vaya arriba y la onda quede plana en el techo.
 */
export function barrasDeMuestras(muestras: Float32Array, cuantas = BARRAS): number[] {
  if (muestras.length === 0 || cuantas <= 0) return [];

  const porBarra = Math.floor(muestras.length / cuantas);
  if (porBarra === 0) return [];

  const crudas: number[] = [];
  for (let i = 0; i < cuantas; i++) {
    let suma = 0;
    for (let j = 0; j < porBarra; j++) suma += Math.abs(muestras[i * porBarra + j] ?? 0);
    crudas.push(suma / porBarra);
  }

  // Se normaliza contra el máximo del propio audio: una nota de voz grabada bajo
  // se vería como una línea si se normalizara contra el máximo teórico.
  const max = Math.max(...crudas);
  if (max === 0) return crudas.map(() => 0);
  return crudas.map((v) => v / max);
}

/**
 * Barras estables derivadas de un identificador.
 *
 * El respaldo cuando no se pudo decodificar. **Determinista**: el mismo audio
 * dibuja siempre la misma onda, porque una que cambiara en cada recarga se
 * leería como un error del panel.
 */
export function barrasDeIdentificador(id: string, cuantas = BARRAS): number[] {
  const barras: number[] = [];
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  for (let i = 0; i < cuantas; i++) {
    h ^= h << 13;
    h ^= h >>> 17;
    h ^= h << 5;
    // Entre 0,25 y 1: una barra casi en cero parecería un hueco en la grabación.
    barras.push(0.25 + (Math.abs(h) % 1000) / 1000 / 1.35);
  }
  return barras;
}
